<template>
  <base-view>
    <div class="page-fill bg-gray-lighter">

      <!-- Hero -->
      <div
        class="relative w-full bg-center bg-cover"
        :style="{ backgroundImage: 'url(' + heroImage + ')' }"
      >
        <div class="absolute inset-0 hero-overlay"></div>
        <section class="relative flex flex-col items-center gap-4 max-w-3xl px-6 pt-14 pb-10 mx-auto text-center">
<h1 class="m-0 text-4xl font-extrabold leading-tight text-gray-900">Plan Videos</h1>
          <p class="max-w-lg m-0 text-lg text-gray-700">
            In-depth overviews of each benefit plan type — helpful for open enrollment and onboarding.
          </p>
        </section>
      </div>

      <!-- Video grid -->
      <div class="max-w-4xl mx-auto px-6 py-10 pb-16">
        <div class="video-grid">
          <button
            v-for="video in videos"
            :key="video.id"
            type="button"
            class="video-card"
            @click="openVideo(video)"
          >
            <div class="video-thumb" aria-hidden="true">
              <img v-if="thumbnails[video.id]" :src="thumbnails[video.id]" :alt="video.title" class="thumb-img" />
              <div class="play-btn">&#9654;</div>
            </div>
            <div class="video-meta">
              <span class="video-title">{{ video.title }}</span>
            </div>
          </button>
        </div>
      </div>

      <!-- Modal -->
      <transition name="modal-fade">
        <div
          v-if="selectedVideo"
          class="modal-backdrop"
          @click.self="closeVideo"
          role="dialog"
          aria-modal="true"
          :aria-label="selectedVideo.title"
        >
          <div class="modal-box">
            <div class="modal-header">
              <span class="modal-title">{{ selectedVideo.title }}</span>
              <button type="button" class="modal-close" @click="closeVideo" aria-label="Close video">✕</button>
            </div>
            <div class="modal-video">
              <iframe
                :src="embedSrc"
                :title="selectedVideo.title"
                allowfullscreen
                frameborder="0"
              ></iframe>
            </div>
          </div>
        </div>
      </transition>

    </div>
  </base-view>
</template>

<script>
import BaseView from "@/components/layout/BaseView.vue";
export default {
  name: "PlanVideos",
  components: { BaseView },
  data() {
    return {
      heroImage: require("@/assets/bg.jpg"),
      selectedVideo: null,
      thumbnails: {},
      videos: [
        { id: "jn0h9tael7",  title: "FSA Overview" },
        { id: "narmv3kr4s",  title: "HSA Overview" },
        { id: "97bsjir1cp",  title: "Limited Purpose FSA Overview" },
        { id: "k4nqf77rxp",  title: "HRA Overview" },
        { id: "5pfymqwdww",  title: "Commuter Overview" },
        { id: "qm5id14mng",  title: "DCA Overview" },
        { id: "zjfzh4rvnd",  title: "FSA / HSA / HRA — Comparison of Accounts" },
        { id: "zn2gnc2gew",  title: "FSA / HSA — What Items Are Eligible?" },
        { id: "rsybt90qxy",  title: "Open Enrollment — Managing Healthcare Finances" },
        { id: "xy41yyou4y",  title: "Open Enrollment — How Much Should I Be Saving?" },
        { id: "5h3nbakrz9",  title: "Open Enrollment — Choosing a Health Insurance Plan" }
      ]
    };
  },
  computed: {
    embedSrc() {
      if (!this.selectedVideo) return "";
      return `https://fast.wistia.net/embed/iframe/${this.selectedVideo.id}?seo=false&playerColor=096843&autoPlay=true`;
    }
  },
  async created() {
    const results = await Promise.all(
      this.videos.map(async v => {
        try {
          const res = await fetch(
            `https://fast.wistia.com/oembed.json?url=https://home.wistia.com/medias/${v.id}`
          );
          const data = await res.json();
          return { id: v.id, url: data.thumbnail_url || null };
        } catch {
          return { id: v.id, url: null };
        }
      })
    );
    const map = {};
    results.forEach(r => { if (r.url) map[r.id] = r.url; });
    this.thumbnails = map;
  },
  methods: {
    openVideo(video) {
      this.selectedVideo = video;
      document.addEventListener("keydown", this.onKeydown);
    },
    closeVideo() {
      this.selectedVideo = null;
      document.removeEventListener("keydown", this.onKeydown);
    },
    onKeydown(e) {
      if (e.key === "Escape") this.closeVideo();
    }
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.onKeydown);
  }
};
</script>

<style scoped>
.page-fill { min-height: calc(100vh - 10rem); }


.hero-overlay {
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.62) 0%,
    rgba(255, 255, 255, 0.88) 45%,
    #fafafa 75%,
    #fafafa 100%
  );
}

/* Grid */
.video-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.25rem;
}
@media (min-width: 640px) {
  .video-grid { grid-template-columns: repeat(3, 1fr); }
}

.video-card {
  display: flex;
  flex-direction: column;
  border-radius: 0.75rem;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  text-align: left;
  font: inherit;
  cursor: pointer;
  padding: 0;
  outline: none;
  -webkit-tap-highlight-color: transparent;
  transition: box-shadow 0.15s, transform 0.15s;
}
.video-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}
.video-card:focus-visible {
  outline: 2px solid #096843;
  outline-offset: 2px;
}

.video-thumb {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: linear-gradient(135deg, #096843, #1c4d78);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  transition: opacity 0.15s;
}
.thumb-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.video-card:hover .video-thumb { opacity: 0.88; }

.play-btn {
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.6);
  color: #ffffff;
  font-size: 1.125rem;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-left: 3px;
  transition: background 0.15s;
}
.video-card:hover .play-btn { background: rgba(255, 255, 255, 0.35); }

.video-meta {
  padding: 0.875rem 1rem;
  flex: 1;
}
.video-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #111827;
  line-height: 1.4;
}
.video-card:hover .video-title { color: #096843; }

/* Modal */
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
}
.modal-box {
  background: #111827;
  border-radius: 0.875rem;
  overflow: hidden;
  width: 100%;
  max-width: 52rem;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.5);
}
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.875rem 1.25rem;
}
.modal-title {
  font-size: 0.9375rem;
  font-weight: 700;
  color: #ffffff;
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.modal-close {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.65);
  font-size: 1rem;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  border-radius: 0.375rem;
  line-height: 1;
  transition: color 0.15s;
  flex-shrink: 0;
}
.modal-close:hover { color: #ffffff; }
.modal-video {
  position: relative;
  aspect-ratio: 16 / 9;
  width: 100%;
}
.modal-video iframe {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
  border: 0;
}

.modal-fade-enter-active,
.modal-fade-leave-active { transition: opacity 0.2s ease; }
.modal-fade-enter,
.modal-fade-leave-to { opacity: 0; }
</style>
